import React, {Suspense, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AssistantScreenshotToolbar, {type CapturePick} from './AssistantScreenshotToolbar';
import type {SelectedRect} from './AssistantScreenshotAreaOverlay';

const AssistantScreenshotEditor = React.lazy(() => import('./AssistantScreenshotEditor'));
const AssistantScreenshotAreaOverlay = React.lazy(() => import('./AssistantScreenshotAreaOverlay'));

// domCapture pulls in snapdom (heavy). Load it on demand so a mounted trigger
// — e.g. an always-present header button — doesn't drag snapdom into the
// initial bundle. import() caches, so it's fetched once on the first capture.
const loadDomCapture = () => import('./domCapture');

// Only one capture session may run at a time across every hook instance (the
// header button and the chat composer each create one). Holds the owner token
// so a blocked instance can never release another's lock.
let captureLockHolder: symbol | null = null;

// idle → picking → (selecting-area) → capturing → editing → idle
type CaptureStage =
    | {kind: 'idle'}
    | {kind: 'picking'}
    | {kind: 'selecting-area'}
    | {kind: 'capturing'; which: CapturePick}
    | {kind: 'editing'; blob: Blob};

export interface UseScreenshotCaptureOptions {
    // user finished editing (the editor's Done button) — receives the flattened
    // PNG. Optional: omit it and Done just closes (e.g. a tool whose only output
    // is the editor's Copy-to-clipboard button).
    onComplete?: (blob: Blob) => void;
    // user backed out of the picker or editor without producing an image
    onCancel?: () => void;
    // capture failed (e.g. snapdom threw). Defaults to console.error so the
    // hook has no hard dependency on any snackbar system.
    onError?: (message: string) => void;
}

export interface ScreenshotCaptureApi {
    // open the capture picker (Select area / Full image)
    beginCapture: () => void;
    // skip capture and open the editor straight on an existing image
    editImage: (blob: Blob) => void;
    // true while this instance's capture/editor overlay is open
    active: boolean;
    // render this anywhere to mount the overlays — they portal to <body>,
    // so the trigger's position in the tree doesn't matter
    overlay: React.ReactNode;
}

// Let React commit the 'capturing' stage — the area overlay unmounts, the
// toolbar shows its spinner — and paint one frame before the DOM snapshot runs.
const waitForCaptureFrame = () =>
    new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/**
 * Screenshot capture + annotate flow as a reusable hook. Owns the picker →
 * select-area → capture → editor state machine and the overlay UI; the caller
 * supplies a trigger (any element calling `beginCapture`) and optionally does
 * something with the finished image via `onComplete`. Used by the chat composer
 * (attach to a message) and the header button (just close), and works anywhere.
 *
 * A module-level lock makes capture single-flight: while one instance has a
 * session open, every other instance's `beginCapture` / `editImage` is a no-op.
 */
export const useScreenshotCapture = (opts: UseScreenshotCaptureOptions): ScreenshotCaptureApi => {
    const {t} = useTranslation();
    const {onComplete, onCancel, onError} = opts;
    const [stage, setStage] = useState<CaptureStage>({kind: 'idle'});

    // latest stage for the imperative entry points, without re-creating them
    const stageRef = useRef(stage);
    stageRef.current = stage;

    // unique per-instance token identifying this hook as the lock owner
    const lockTokenRef = useRef<symbol | null>(null);
    if (lockTokenRef.current === null) lockTokenRef.current = Symbol('screenshot-capture');

    const acquireLock = useCallback((): boolean => {
        if (captureLockHolder && captureLockHolder !== lockTokenRef.current) return false;
        captureLockHolder = lockTokenRef.current;
        return true;
    }, []);
    const releaseLock = useCallback(() => {
        if (captureLockHolder === lockTokenRef.current) captureLockHolder = null;
    }, []);
    // release if we still hold the lock when unmounting mid-session
    useEffect(() => () => releaseLock(), [releaseLock]);

    const fail = useCallback((message: string) => {
        if (onError) onError(message);
        else console.error(message);
        // only the in-flight capture is aborted; an open editor is left alone
        setStage(curr => (curr.kind === 'capturing' ? {kind: 'idle'} : curr));
        releaseLock();
    }, [onError, releaseLock]);

    const beginCapture = useCallback(() => {
        if (stageRef.current.kind !== 'idle') return;
        if (!acquireLock()) return; // another capture session is already open
        // warm the snapdom chunk while the user reads the picker, so the
        // actual capture isn't waiting on a cold fetch
        void loadDomCapture();
        setStage({kind: 'picking'});
    }, [acquireLock]);

    const editImage = useCallback((blob: Blob) => {
        if (stageRef.current.kind !== 'idle') return;
        if (!acquireLock()) return;
        setStage({kind: 'editing', blob});
    }, [acquireLock]);

    const cancel = useCallback(() => {
        setStage({kind: 'idle'});
        releaseLock();
        onCancel?.();
    }, [onCancel, releaseLock]);

    // "Full image" — capture the whole visible viewport.
    const handlePick = async (pick: CapturePick) => {
        if (stage.kind === 'capturing') return;
        if (pick === 'select-area') {
            if (stage.kind !== 'selecting-area') setStage({kind: 'selecting-area'});
            return;
        }
        setStage({kind: 'capturing', which: 'full-image'});
        await waitForCaptureFrame();
        try {
            const {captureViewportDom} = await loadDomCapture();
            const blob = await captureViewportDom();
            setStage(curr => (curr.kind === 'capturing' ? {kind: 'editing', blob} : curr));
        } catch {
            fail(t('Screenshot capture failed.'));
        }
    };

    // "Select area" — capture just the region the user dragged out.
    const handleConfirmArea = useCallback(async (rect: SelectedRect) => {
        setStage({kind: 'capturing', which: 'select-area'});
        await waitForCaptureFrame();
        try {
            const {captureRegionDom} = await loadDomCapture();
            const blob = await captureRegionDom(rect);
            setStage(curr => (curr.kind === 'capturing' ? {kind: 'editing', blob} : curr));
        } catch {
            fail(t('Screenshot capture failed.'));
        }
    }, [fail, t]);

    const handleEditorDone = useCallback((blob: Blob) => {
        setStage({kind: 'idle'});
        releaseLock();
        onComplete?.(blob);
    }, [onComplete, releaseLock]);

    const overlay = (
        <>
            {(stage.kind === 'picking'
                || stage.kind === 'selecting-area'
                || stage.kind === 'capturing') && (
                <AssistantScreenshotToolbar
                    onPick={handlePick}
                    onCancel={cancel}
                    capturing={stage.kind === 'capturing' ? stage.which : undefined}
                />
            )}

            {stage.kind === 'selecting-area' && (
                <Suspense fallback={null}>
                    <AssistantScreenshotAreaOverlay onConfirm={handleConfirmArea}/>
                </Suspense>
            )}

            {stage.kind === 'editing' && (
                <Suspense fallback={null}>
                    <AssistantScreenshotEditor
                        image={stage.blob}
                        onDone={handleEditorDone}
                        onCancel={cancel}
                    />
                </Suspense>
            )}
        </>
    );

    return {beginCapture, editImage, active: stage.kind !== 'idle', overlay};
};
